// @flow

import DefaultButton from "@cabonline/shared/components/buttons/DefaultButton"
import Dropdown from "@cabonline/shared/components/Dropdown"
import { Affix, Col, DatePicker, Input, Row, Table } from "antd"
// Icons
import clearIcon from "apps/orders/icons/clear.svg"
import toggleFiltersIcon from "apps/orders/icons/filters.svg"
import OrderSearchUtils from "apps/orders/OrderSearchUtils"
import OrderSummaryView from "apps/orders/OrderSummaryView"
// External libraries
import { get } from "lodash"
import { observer } from "mobx-react-lite"
import moment from "moment"
import * as queryString from "query-string"
import React, { useEffect, useState } from "react"
import { injectIntl } from "react-intl"
import InfiniteScroll from "shared/components/InfiniteScroll"
import LoadingSpin from "shared/components/LoadingSpin"
import PermissionGuard from "shared/components/PermissionGuard"
import PhoneInputFallback from "shared/components/PhoneInputFallback"
import { TitleSecondary } from "shared/components/text"
import useDebounce from "shared/hooks/useDebounce"
import BrandsStore from "shared/stores/BrandsStore"
import FleetsStore from "shared/stores/FleetsStore"
import IntlStore from "shared/stores/IntlStore"
import OrderSearchStore from "shared/stores/OrderSearchStore"
import { toTitleCase } from "shared/utils/stringUtils"
import styled from "styled-components"

import { BOOKING_CHANNELS, ORDER_STATES } from "./constants"
// Styles
import styles from "./OrderSearchView.scss"

const FieldsRow = styled(Row)`
  margin-bottom: 20px;
  padding-right: 20px;
`

const SearchButtonsNew = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
`

const FilterButton = styled(DefaultButton)`
  background-color: rgb(236, 236, 236) !important;
  border-color: rgb(236, 236, 236) !important;
`

const SearchWrapperDiv = styled.div`
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.03);
  padding: 20px;
  margin-bottom: 15px;
`

const ContentRow = styled(Row)`
  margin-top: 15px;
`

const ResultTable = styled(Table)`
  border-radius: 6px;
`

const OrderSearchView = props => {
  const {
    intl: { formatMessage },
    history,
    location
  } = props

  const { selectedOrder, totalCount } = OrderSearchStore
  const [isParamsMapped, setIsParamsMapped] = useState(false)
  const [fetching, setFetching] = useState(false)

  const [orderId, setOrderId] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [fromAddress, setFromAddress] = useState("")
  const [toAddress, setToAddress] = useState("")
  const [startDate, setStartDate] = useState(undefined)
  const [endDate, setEndDate] = useState(undefined)
  const [source, setSource] = useState(undefined)
  const [orderState, setOrderState] = useState(undefined)
  const [brand, setBrand] = useState(undefined)
  const [fleet, setFleet] = useState(undefined)
  const [customerName, setCustomerName] = useState("")

  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [hasPendingAjaxRequests, setHasPendingAjaxRequests] = useState(false)
  const [pendingRequestCount, setPendingRequestCount] = useState(0)

  useEffect(() => {
    setIsParamsMapped(false)
    const params = queryString.parse(location.search, { arrayFormat: "comma" })
    setOrderId(params.orderId || "")
    setPhoneNumber(params.phoneNumber || "")
    setFromAddress(params.fromAddress || "")
    setToAddress(params.toAddress || "")
    if (params.startDate && params.endDate)
      setDate([moment(params.startDate), moment(params.endDate)])
    else setDate([undefined, undefined])
    setSource(params.source)
    setOrderState(params.orderState)
    setBrand(params.brand)
    setFleet(params.fleet)
    setCustomerName(params.customer || "")
    setIsParamsMapped(true)
  }, [location.search])

  useEffect(() => {
    if (window.location.href.includes("iframe")) {
      const params = queryString.parse(location.search, {
        arrayFormat: "comma"
      })
      IntlStore.setSelectedLanguage(params["lang"] || "sv")
    }
  }, [])

  const updateQueryString = (value, param) => {
    const params = queryString.parse(location.search, { arrayFormat: "comma" })

    if (param === "dates") {
      if (value.length) {
        params["startDate"] = value[0].toISOString()
        params["endDate"] = value[1].toISOString()
      } else {
        delete params["startDate"]
        delete params["endDate"]
      }
    } else {
      if (value) params[param] = value
      else delete params[param]
    }

    const queryStringParams = queryString.stringify(params, {
      arrayFormat: "comma"
    })
    history.push(`${location.pathname}?${queryStringParams}`)
  }

  const debouncedFromAddress = useDebounce(fromAddress, 300)
  const debouncedToAddress = useDebounce(toAddress, 300)
  const debouncedOrderId = useDebounce(orderId, 300)
  const debouncedPhoneNumber = useDebounce(phoneNumber, 300)
  const debouncedCustomerName = useDebounce(customerName, 300)

  const isInitialLengthFulfilled = () => {
    return (
      orderId.length > 4 ||
      fromAddress.length > 4 ||
      toAddress.length > 4 ||
      phoneNumber.length > 4 ||
      customerName.length > 2
    )
  }

  const searchOrders = (queryChange: boolean) => {
    if (queryChange) {
      OrderSearchStore.setSelectedOrder({})
    }
    setFetching(true)
    OrderSearchStore.searchWithFromIndex({
      orderId: orderId,
      phoneNumber: phoneNumber,
      fromAddress: fromAddress,
      toAddress: toAddress,
      startDate: startDate,
      endDate: endDate,
      source: source,
      state: orderState,
      brand: brand,
      fleet: fleet,
      customerName: customerName,
      isFetchMore: !queryChange
    }).then(() => {
      setFetching(false)
    })
  }

  useEffect(() => {
    // Make sure we have a value (user has entered something in input)
    if (
      source ||
      orderState ||
      brand ||
      fleet ||
      ((debouncedFromAddress ||
        debouncedToAddress ||
        debouncedOrderId ||
        debouncedPhoneNumber ||
        debouncedCustomerName) &&
        isParamsMapped &&
        isInitialLengthFulfilled())
    ) {
      OrderSearchStore.setSelectedOrder({})
      // Set fetching state
      setFetching(true)
      // Fire off our API call
      OrderSearchStore.searchWithFromIndex({
        orderId: orderId,
        phoneNumber: phoneNumber,
        fromAddress: fromAddress,
        toAddress: toAddress,
        startDate: startDate,
        endDate: endDate,
        source: source,
        state: orderState,
        brand: brand,
        fleet: fleet,
        customerName: customerName,
        isFetchMore: false
      }).then(() => {
        // Set state back to false since request finished
        setFetching(false)
      })
    }
  }, [
    debouncedFromAddress,
    debouncedToAddress,
    debouncedOrderId,
    debouncedPhoneNumber,
    debouncedCustomerName,
    source,
    orderState,
    brand,
    fleet
  ])

  const addPendingRequestToQueue = () => {
    setPendingRequestCount(prevCount => prevCount + 1)
    setHasPendingAjaxRequests(pendingRequestCount !== 0)
  }

  const reducePendingRequestFromQueue = () => {
    setPendingRequestCount(prevCount => prevCount - 1)
    setHasPendingAjaxRequests(pendingRequestCount !== 0)
  }

  useEffect(() => {
    addPendingRequestToQueue()
    BrandsStore.getFleetBrands()
      .then(() => {
        reducePendingRequestFromQueue()
      })
      .catch(() => {
        reducePendingRequestFromQueue()
      })
    addPendingRequestToQueue()
    FleetsStore.getAllFleets()
      .then(() => {
        reducePendingRequestFromQueue()
      })
      .catch(() => {
        reducePendingRequestFromQueue()
      })
  }, [])

  const onDateFilterChange = () => {
    searchOrders(true)
  }

  const onKeyPressOnCustomerName = e => {
    if (e.keyCode === 13) {
      searchOrders(true)
    }
  }

  const setDate = ([startDate, endDate]: Array<Object>) => {
    setStartDate(startDate)
    setEndDate(endDate)
  }

  const onRowClick = order => {
    OrderSearchStore.setSelectedOrder(order)
  }

  const clearAllFilters = () => {
    OrderSearchStore.setSelectedOrder({})
    OrderSearchStore.setOrders([])
    OrderSearchStore.setCurrentPage(1)
    OrderSearchStore.setTotalCount(0)
    history.push(`${location.pathname}`)
  }

  const onToggleShowMoreFiltersClicked = () => {
    setShowMoreFilters(!showMoreFilters)
  }

  const renderAdditionalSearchFields = () => {
    const brandOptions = BrandsStore.brands.map(brand => ({
      value: brand.shortName,
      label: brand.name
    }))
    const fleetOptions = FleetsStore.fleets.map(fleet => ({
      value: fleet.id,
      label: fleet.name
    }))

    return (
      <Row>
        <FieldsRow type="flex" justify="start" gutter={20}>
          <Col xs={0} sm={0} lg={4}>
            {formatMessage({ id: "apps.order-search.search.bookingChannel" })}
          </Col>
          <Col xs={0} sm={0} lg={4}>
            {formatMessage({ id: "apps.order-search.search.orderState" })}
          </Col>
          <Col xs={0} sm={0} lg={4}>
            {formatMessage({ id: "common.brands" })}
          </Col>
          <Col xs={0} sm={0} lg={6}>
            {formatMessage({ id: "common.fleet" })}
          </Col>
          <Col xs={0} sm={0} lg={6}>
            {formatMessage({ id: "common.customer" })}
          </Col>
        </FieldsRow>
        <FieldsRow type="flex" justify="start" gutter={20}>
          <Col xs={24} sm={24} lg={4}>
            <div data-cy="cy_bookingChannelFilter">
              <Dropdown
                onChange={val => updateQueryString(val, "source")}
                placeholder={formatMessage({ id: "common.all" })}
                value={source}
                options={BOOKING_CHANNELS}
                allowClear={true}
              />
            </div>
          </Col>
          <Col xs={24} sm={24} lg={4}>
            <div data-cy="cy_orderStateFilter">
              <Dropdown
                onChange={val => updateQueryString(val, "orderState")}
                placeholder={formatMessage({ id: "common.all" })}
                value={orderState}
                options={ORDER_STATES}
                allowClear={true}
              />
            </div>
          </Col>
          <Col xs={24} sm={24} lg={4}>
            <div data-cy="cy_brandOptionsFilter">
              <Dropdown
                onChange={brand => updateQueryString(brand, "brand")}
                placeholder={formatMessage({ id: "common.all" })}
                options={brandOptions}
                value={brand}
                allowClear
              />
            </div>
          </Col>
          <Col xs={24} sm={24} lg={6}>
            <div data-cy="cy_fleetOptionsFilter">
              <Dropdown
                onChange={fleet => updateQueryString(fleet, "fleet")}
                placeholder={formatMessage({ id: "common.fleet" })}
                options={fleetOptions}
                value={fleet && fleet.name}
                allowClear
              />
            </div>
          </Col>
          <Col xs={24} sm={24} lg={6}>
            <Input
              data-cy="cy_customerNameFilter"
              placeholder={formatMessage({ id: "common.customer" })}
              value={customerName}
              onChange={e => updateQueryString(e.target.value, "customer")}
              onKeyDown={e => onKeyPressOnCustomerName(e)}
            />
          </Col>
        </FieldsRow>
      </Row>
    )
  }

  const renderDefaultSearchFields = () => {
    return (
      <Row>
        <FieldsRow type="flex" justify="start" gutter={20}>
          <Col xs={0} sm={0} lg={4}>
            {formatMessage({ id: "common.orderId" })}
          </Col>
          <Col xs={0} sm={0} lg={4}>
            {formatMessage({ id: "apps.order-search.search.phoneNumber" })}
          </Col>
          <Col xs={0} sm={0} lg={5}>
            {formatMessage({ id: "apps.order-search.search.fromAddress" })}
          </Col>
          <Col xs={0} sm={0} lg={5}>
            {formatMessage({ id: "apps.order-search.search.toAddress" })}
          </Col>
          <Col xs={0} sm={0} lg={6}>
            {formatMessage({ id: "common.date" })}
          </Col>
        </FieldsRow>
        <FieldsRow type="flex" justify="start" gutter={20}>
          <Col xs={24} sm={24} lg={4}>
            <Input
              placeholder={formatMessage({ id: "common.orderId" })}
              value={orderId}
              onChange={e =>
                updateQueryString(e.target.value.trim(), "orderId")
              }
            />
          </Col>
          <Col xs={24} sm={24} lg={4}>
            <PhoneInputFallback
              data-cy="cy_phoneNumberFilter"
              onChange={val => updateQueryString(val, "phoneNumber")}
              value={phoneNumber}
              placeholder={formatMessage({ id: "common.phoneNumber" })}
            />
          </Col>
          <Col xs={24} sm={24} lg={5}>
            <Input
              placeholder={formatMessage({
                id: "apps.order-search.search.fromAddress"
              })}
              value={fromAddress}
              onChange={e => updateQueryString(e.target.value, "fromAddress")}
            />
          </Col>
          <Col xs={24} sm={24} lg={5}>
            <Input
              placeholder={formatMessage({
                id: "apps.order-search.search.toAddress"
              })}
              value={toAddress}
              onChange={e => updateQueryString(e.target.value, "toAddress")}
            />
          </Col>
          <Col xs={24} sm={24} lg={6}>
            <DatePicker.RangePicker
              data-cy="cy_orderSearchDatePicker"
              onChange={dates => updateQueryString(dates, "dates")}
              onOk={onDateFilterChange}
              value={[startDate, endDate]}
              disabled={false}
              showTime={{
                hideDisabledOptions: true,
                defaultValue: [
                  moment("00:00", "HH:mm"),
                  moment("23:59", "HH:mm")
                ]
              }}
              format="YYYY-MM-DD"
              placeholder={["Start date", "End date"]}
              style={{ width: "100%" }}
            />
          </Col>
        </FieldsRow>
        {showMoreFilters && renderAdditionalSearchFields()}
        <SearchButtonsNew>
          <FilterButton onClick={clearAllFilters}>
            <img
              src={clearIcon}
              width={18}
              style={{ marginRight: "5px" }}
              alt="clear all"
            />
            {formatMessage({ id: "apps.order-search.search.clearallfilters" })}
          </FilterButton>
          <FilterButton onClick={onToggleShowMoreFiltersClicked}>
            <img
              src={toggleFiltersIcon}
              width={18}
              style={{ marginRight: "5px" }}
              alt="show/hide filters"
            />
            {showMoreFilters
              ? formatMessage({
                  id: "apps.order-search.search.showlessfilters"
                })
              : formatMessage({
                  id: "apps.order-search.search.showmorefilters"
                })}
          </FilterButton>
        </SearchButtonsNew>
      </Row>
    )
  }

  const getColumns = () => {
    const {
      intl: { formatMessage }
    } = props

    return [
      {
        title: "",
        dataIndex: "state",
        width: 35,
        align: "center",
        render: text => {
          return (
            <img
              src={OrderSearchUtils.renderStatusIcon(text)}
              width={20}
              alt="status"
            />
          )
        }
      },
      {
        title: formatMessage({
          id: "apps.order-search.search-result.order-id"
        }),
        width: 100,
        dataIndex: "publicId"
      },
      {
        title: formatMessage({
          id: "apps.order-search.search-result.traveler-phone-no"
        }),
        maxWidth: 150,
        dataIndex: "travelerPhoneNumber",
        render: text => {
          return text == null ? (
            <div style={{ textAlign: "center" }}>-</div>
          ) : (
            text
          )
        }
      },
      {
        title: formatMessage({
          id: "apps.order-search.search-result.traveler-name"
        }),
        dataIndex: "travelerName",
        render: toTitleCase
      },
      {
        title: formatMessage({
          id: "apps.order-search.search-result.pickup-time"
        }),
        width: 161,
        dataIndex: "pickupTime"
      },
      {
        title: formatMessage({
          id: "apps.order-search.label.from"
        }),
        dataIndex: "fromAddress",
        render: text => OrderSearchUtils.addressObjToText(text)
      },
      {
        title: formatMessage({
          id: "apps.order-search.label.to"
        }),
        className: "collapsible",
        dataIndex: "toAddress",
        render: text => OrderSearchUtils.addressObjToText(text)
      }
    ]
  }

  const columns = getColumns()

  const callSearchHasActiveQuery = () => {
    if (
      !OrderSearchStore.isFetchingOrders &&
      OrderSearchStore.hasMoreOrders &&
      OrderSearchStore.orders.length > 0
    ) {
      searchOrders(false)
    }
  }

  const setRowClassName = record => {
    return record.orderId === get(selectedOrder, "orderId")
      ? "selected-row"
      : ""
  }

  return (
    <div>
      <PermissionGuard has={["orders:order-search"]}>
        {hasPendingAjaxRequests && (
          <LoadingSpin spinStyle={{ position: "fixed" }} />
        )}
        <SearchWrapperDiv>{renderDefaultSearchFields()}</SearchWrapperDiv>
        <TitleSecondary>{`${totalCount} ${formatMessage({
          id: "common.orders"
        })}`}</TitleSecondary>
        <ContentRow>
          <Col span={18}>
            <InfiniteScroll
              fetchMore={() => {
                callSearchHasActiveQuery()
              }}
            >
              <ResultTable
                className={styles.table}
                loading={fetching}
                dataSource={OrderSearchStore.orders}
                columns={columns}
                disableSort
                emptyState={formatMessage({
                  id: "apps.order-search.list.empty"
                })}
                onRow={record => ({
                  onClick: () => onRowClick(record)
                })}
                rowClassName={setRowClassName}
                rowKey="orderId"
                pagination={false}
              />
            </InfiniteScroll>
          </Col>
          <Col span={6}>
            <Affix offsetTop={80}>
              <OrderSummaryView
                addPendingRequestToQueue={addPendingRequestToQueue}
                reducePendingRequestFromQueue={reducePendingRequestFromQueue}
              />
            </Affix>
          </Col>
        </ContentRow>
      </PermissionGuard>
    </div>
  )
}

export default (injectIntl(observer(OrderSearchView)): any)
